/**
 * Simple task polling utility that doesn't require hooks
 */

const apiBaseUrl = process.env.REACT_APP_API_URL_BASE

class TaskPoller {
  constructor() {
    this.activePolls = new Map() // taskId -> { intervalId, onStatusChange }
  }

  /**
   * Start polling a task
   * @param {string} taskId - The task ID to poll
   * @param {string} orgAccessToken - Organization access token
   * @param {function} onStatusChange - Callback when status changes (taskId, status)
   * @param {number} intervalMs - Polling interval in milliseconds (default: 2000)
   */
  startPolling(taskId, orgAccessToken, onStatusChange, intervalMs = 2000) {
    if (!taskId || !orgAccessToken) {
      console.warn('Cannot start polling: missing taskId or orgAccessToken')
      return
    }

    // Stop existing polling for this task if any
    this.stopPolling(taskId)

    console.log(`Starting polling for task ${taskId}`)

    const pollFunction = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/tasks/${taskId}/status`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${orgAccessToken}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const data = await response.json()
          const state = data.state

          console.log(`Task ${taskId} state:`, state)

          // Call the state change callback
          if (onStatusChange) {
            onStatusChange(taskId, state)
          }

          // Stop polling for terminal states
          if (['SUCCESS', 'ERROR', 'FAILURE'].includes(state)) {
            this.stopPolling(taskId)
          }
        } else {
          console.error(`Failed to poll task ${taskId}:`, response.statusText)
          this.stopPolling(taskId)
        }
      } catch (error) {
        console.error(`Error polling task ${taskId}:`, error)
        // Don't stop polling on network errors, let it retry
      }
    }

    // Poll immediately, then at intervals
    pollFunction()
    const intervalId = setInterval(pollFunction, intervalMs)

    // Store the polling info
    this.activePolls.set(taskId, {
      intervalId,
      onStatusChange
    })
  }

  /**
   * Stop polling a specific task
   * @param {string} taskId - The task ID to stop polling
   */
  stopPolling(taskId) {
    const pollInfo = this.activePolls.get(taskId)
    if (pollInfo) {
      console.log(`Stopping polling for task ${taskId}`)
      clearInterval(pollInfo.intervalId)
      this.activePolls.delete(taskId)
      return true
    } else {
      console.warn(`No active polling found for task ${taskId}`)
      return false
    }
  }

  /**
   * Force stop polling for a specific task (more aggressive)
   * @param {string} taskId - The task ID to force stop
   */
  forceStopPolling(taskId) {
    console.log(`Force stopping polling for task ${taskId}`)
    
    const pollInfo = this.activePolls.get(taskId)
    if (pollInfo) {
      try {
        clearInterval(pollInfo.intervalId)
      } catch (error) {
        console.error(`Error clearing interval for task ${taskId}:`, error)
      }
      this.activePolls.delete(taskId)
      console.log(`Successfully force stopped polling for task ${taskId}`)
      return true
    } else {
      console.warn(`No active polling found for task ${taskId} to force stop`)
      return false
    }
  }

  /**
   * Stop all active polling
   */
  stopAllPolling() {
    console.log('Stopping all task polling')
    for (const [taskId, pollInfo] of this.activePolls.entries()) {
      clearInterval(pollInfo.intervalId)
    }
    this.activePolls.clear()
  }

  /**
   * Get list of currently polling task IDs
   */
  getActiveTaskIds() {
    return Array.from(this.activePolls.keys())
  }

  /**
   * Check if a specific task is being polled
   * @param {string} taskId - The task ID to check
   */
  isPolling(taskId) {
    return this.activePolls.has(taskId)
  }
  
  /**
   * Get detailed polling information for debugging
   */
  getPollingDetails() {
    const details = {}
    for (const [taskId, pollInfo] of this.activePolls.entries()) {
      details[taskId] = {
        hasIntervalId: !!pollInfo.intervalId,
        hasCallback: !!pollInfo.onStatusChange,
        intervalId: pollInfo.intervalId
      }
    }
    return {
      activeCount: this.activePolls.size,
      details
    }
  }

  /**
   * Force stop all polling with detailed cleanup
   */
  forceStopAllPolling() {
    console.log(`Force stopping all ${this.activePolls.size} active polling tasks`)
    
    const stopped = []
    const errors = []
    
    for (const [taskId, pollInfo] of this.activePolls.entries()) {
      try {
        clearInterval(pollInfo.intervalId)
        stopped.push(taskId)
      } catch (error) {
        console.error(`Error stopping polling for task ${taskId}:`, error)
        errors.push({ taskId, error: error.message })
      }
    }
    
    this.activePolls.clear()
    
    console.log(`Force stopped ${stopped.length} tasks, ${errors.length} errors`)
    return { stopped, errors }
  }
}

// Export a singleton instance
export const taskPoller = new TaskPoller()